/**
 * view_beacon_controller.js — records that a person opened a public document.
 *
 * The quote and invoice pages are reached from an SMS or email link, and every
 * messaging client that renders a link preview fetches that URL before any human
 * sees it: Apple's Messages on the sender's and receiver's devices, Google
 * Messages through its own servers, mail scanners on theirs. Those fetchers run
 * no JavaScript (Apple TN3156), which is the one property that separates them
 * from a person, so the server cannot tell from the GET alone whether to stamp a
 * view and ring the tradie's bell for a document nobody has read.
 *
 * This controller is that separator. It POSTs the view, so only a browser that
 * actually executed script records one; anything that merely fetched the page
 * records nothing. That is the safe direction to fail: a missed view stays
 * quiet, a fabricated one wakes someone up.
 *
 * The server owns the dedup — each service stamps only the first open — so
 * reconnecting after a Turbo navigation or a reload is harmless.
 *
 * Values:
 *   url   — the document's seen endpoint, rendered by the template
 *   token — the page's csrf_token, because the beacon POSTs from a public page
 *           that has no session to authenticate with
 *
 * Fire-and-forget: connect() adds no listeners, observers or timers, so
 * disconnect() has nothing to undo.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static values = { url: String, token: String };

    connect() {
        if (!this.urlValue) return;
        const body = new FormData();
        body.append("csrf_token", this.tokenValue);
        // keepalive lets the beacon finish when the client navigates straight
        // back out. A failure is swallowed deliberately (see the header): the
        // worst case is that no view is recorded, never a false one.
        fetch(this.urlValue, { method: "POST", body, keepalive: true }).catch(() => null);
    }
}
